import { useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts-pro';
import { useMemo } from 'react';
import { CardPaper, formatCompact } from '../lib';
import { CardHeader } from './CardHeader';
import { ChartDataTable } from './ChartDataTable';

interface HorizontalBarCardProps {
  title: string;
  labels: string[];
  values: number[];
  tooltip?: string;
  onBarClick?: (index: number) => void;
}

const MAX_LABEL_CHARS = 22;

const truncate = (label: string) =>
  label.length > MAX_LABEL_CHARS ? `${label.slice(0, MAX_LABEL_CHARS - 1)}…` : label;

export const HorizontalBarCard = ({
  title,
  labels,
  values,
  tooltip,
  onBarClick,
}: HorizontalBarCardProps) => {
  const theme = useTheme();

  // Index the y-axis numerically so duplicate labels (two distinct entities
  // sharing a display name) get distinct band positions; valueFormatter maps
  // each index back to its label string for display and tooltips.
  const indices = useMemo(() => labels.map((_, i) => i), [labels]);

  return (
    <CardPaper>
      <CardHeader title={title} tooltip={tooltip} />
      <ChartDataTable labels={labels} title={title} values={values} />
      <BarChart
        layout="horizontal"
        yAxis={[
          {
            scaleType: 'band',
            data: indices,
            valueFormatter: (idx: number, ctx) => {
              const label = labels[idx] ?? '';
              return ctx.location === 'tick' ? truncate(label) : label;
            },
          },
        ]}
        xAxis={[{ valueFormatter: formatCompact }]}
        series={[{ data: values, color: theme.palette.primary.main }]}
        height={300}
        margin={{ left: 160, right: 16, top: 16, bottom: 32 }}
        slotProps={{ legend: { hidden: true } }}
        onAxisClick={
          onBarClick &&
          ((_, params) => {
            if (params?.dataIndex != null) onBarClick(params.dataIndex);
          })
        }
        sx={
          onBarClick && {
            '& .MuiBarElement-root': { cursor: 'pointer' },
          }
        }
      />
    </CardPaper>
  );
};
